// =============================================================================
// VINUM - UI Store
// Global UI state management (theme, toasts, modals, etc.)
// =============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ThemeMode, Toast, ToastType, Breakpoint } from '../types';
import { STORAGE_KEYS, getBreakpoint } from '../constants';
import { generateId } from '../utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface UIState {
  // Theme
  themeMode: ThemeMode;
  isDark: boolean;

  // Toasts
  toasts: Toast[];

  // Responsive
  breakpoint: Breakpoint;
  windowWidth: number;

  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Loading overlay
  globalLoading: boolean;
  globalLoadingMessage: string | null;

  // Modals
  activeModal: string | null;
  modalData: unknown;
}

interface UIActions {
  // Theme
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;

  // Toasts
  showToast: (type: ToastType, title: string, message?: string, duration?: number) => void;
  showSuccessToast: (title: string, message?: string) => void;
  showErrorToast: (title: string, message?: string) => void;
  showWarningToast: (title: string, message?: string) => void;
  showInfoToast: (title: string, message?: string) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Responsive
  setWindowWidth: (width: number) => void;

  // Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;

  // Loading
  setGlobalLoading: (loading: boolean, message?: string) => void;

  // Modals
  openModal: (modalId: string, data?: unknown) => void;
  closeModal: () => void;
}

type UIStore = UIState & UIActions;

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function getSystemTheme(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function computeIsDark(mode: ThemeMode): boolean {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode === 'dark';
}

// -----------------------------------------------------------------------------
// Store Implementation
// -----------------------------------------------------------------------------

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      // Initial state
      themeMode: 'system',
      isDark: false,
      toasts: [],
      breakpoint: 'desktop',
      windowWidth: 1024,
      sidebarOpen: true,
      sidebarCollapsed: false,
      globalLoading: false,
      globalLoadingMessage: null,
      activeModal: null,
      modalData: null,

      // Theme actions
      setThemeMode: (mode: ThemeMode) => {
        set({
          themeMode: mode,
          isDark: computeIsDark(mode),
        });
      },

      toggleTheme: () => {
        const current = get().themeMode;
        const newMode = current === 'light' ? 'dark' : 'light';
        get().setThemeMode(newMode);
      },

      // Toast actions
      showToast: (type: ToastType, title: string, message?: string, duration = 4000) => {
        const id = generateId();
        const toast: Toast = { id, type, title, message, duration };

        set((state) => ({
          toasts: [...state.toasts, toast],
        }));

        // Auto-remove after duration
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, duration);
        }
      },

      showSuccessToast: (title: string, message?: string) => {
        get().showToast('success', title, message);
      },

      showErrorToast: (title: string, message?: string) => {
        get().showToast('error', title, message, 6000);
      },

      showWarningToast: (title: string, message?: string) => {
        get().showToast('warning', title, message);
      },

      showInfoToast: (title: string, message?: string) => {
        get().showToast('info', title, message);
      },

      removeToast: (id: string) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      clearToasts: () => {
        set({ toasts: [] });
      },

      // Responsive actions
      setWindowWidth: (width: number) => {
        set({
          windowWidth: width,
          breakpoint: getBreakpoint(width),
        });
      },

      // Sidebar actions
      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      toggleSidebarCollapsed: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      // Loading actions
      setGlobalLoading: (loading: boolean, message?: string) => {
        set({
          globalLoading: loading,
          globalLoadingMessage: loading ? message || null : null,
        });
      },

      // Modal actions
      openModal: (modalId: string, data?: unknown) => {
        set({
          activeModal: modalId,
          modalData: data,
        });
      },

      closeModal: () => {
        set({
          activeModal: null,
          modalData: null,
        });
      },
    }),
    {
      name: STORAGE_KEYS.THEME_MODE,
      storage: createJSONStorage(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
          return localStorage;
        }
        return {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {},
        };
      }),
      partialize: (state) => ({
        themeMode: state.themeMode,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        // Recompute isDark after rehydration
        if (state) {
          state.isDark = computeIsDark(state.themeMode);
        }
      },
    }
  )
);

// -----------------------------------------------------------------------------
// Selectors
// -----------------------------------------------------------------------------

export const selectThemeMode = (state: UIStore) => state.themeMode;
export const selectIsDark = (state: UIStore) => state.isDark;
export const selectToasts = (state: UIStore) => state.toasts;
export const selectBreakpoint = (state: UIStore) => state.breakpoint;
export const selectWindowWidth = (state: UIStore) => state.windowWidth;
export const selectSidebarOpen = (state: UIStore) => state.sidebarOpen;
export const selectSidebarCollapsed = (state: UIStore) => state.sidebarCollapsed;
export const selectGlobalLoading = (state: UIStore) => state.globalLoading;
export const selectActiveModal = (state: UIStore) => state.activeModal;

// Convenience selectors for breakpoints
export const selectIsMobile = (state: UIStore) =>
  state.breakpoint === 'small' || state.breakpoint === 'mobile';
export const selectIsTablet = (state: UIStore) => state.breakpoint === 'tablet';
export const selectIsDesktop = (state: UIStore) => state.breakpoint === 'desktop';
